import * as THREE from 'three';
import { Shape, type Particles } from './Particles';

const _c = new THREE.Color();
const rnd = (a: number, b: number) => a + Math.random() * (b - a);
/** A light, shared breeze so every ember in the courtyard drifts the same way. */
const WIND_X = 0.14, WIND_Z = -0.05;

/** An open flame: embers per second, and optionally tongues of fire per second at a given size. */
export interface Flame { pos: THREE.Vector3; color: number; rate: number; radius: number; fire?: number; fireSize?: number }

/**
 * Background life: open flames shed embers on a fixed budget (a few dozen
 * particles per second in total). Runs in game time, so hit-stop and slow
 * motion freeze the air along with everything else.
 */
export class Ambience {
  private readonly acc: Float32Array;
  private readonly fireAcc: Float32Array;

  constructor(private readonly p: Particles, private readonly flames: Flame[], private readonly density = 1) {
    this.acc = new Float32Array(flames.length).map(() => Math.random());
    this.fireAcc = new Float32Array(flames.length).map(() => Math.random());
  }

  update(dt: number) {
    for (let i = 0; i < this.flames.length; i++) {
      const f = this.flames[i];
      if (f.fire) {
        // tongues of fire: noise-eroded puffs that rise, lean on the breeze and cool into soot
        this.fireAcc[i] += dt * f.fire * (f.radius < 0.2 ? 0.6 : 1) * Math.max(0.5, this.density);
        while (this.fireAcc[i] >= 1) {
          this.fireAcc[i] -= 1;
          const a = Math.random() * Math.PI * 2, r = Math.sqrt(Math.random()) * f.radius * 0.6, s = f.fireSize ?? 0.2;
          this.p.emit({ x: f.pos.x + Math.cos(a) * r, y: f.pos.y - s * 0.4, z: f.pos.z + Math.sin(a) * r,
            vx: WIND_X * 0.6 + rnd(-0.05, 0.05), vy: rnd(0.35, 0.6), vz: WIND_Z * 0.6 + rnd(-0.05, 0.05),
            color: _c.set(f.color), intensity: 1.2, life: rnd(0.45, 0.7), size: s * 0.6, sizeEnd: s * rnd(0.9, 1.2),
            gravity: -1.2, drag: 0.8, shape: Shape.Fire, stretch: 0.01, fade: 1, body: 0.35 });
        }
      }
      this.acc[i] += dt * f.rate * this.density;
      while (this.acc[i] >= 1) {
        this.acc[i] -= 1;
        const a = Math.random() * Math.PI * 2, r = Math.sqrt(Math.random()) * f.radius;
        const hot = Math.random() < 0.25;
        this.p.emit({
          x: f.pos.x + Math.cos(a) * r, y: f.pos.y + rnd(0, 0.06), z: f.pos.z + Math.sin(a) * r,
          vx: WIND_X + rnd(-0.12, 0.12), vy: rnd(0.35, 0.8), vz: WIND_Z + rnd(-0.1, 0.1),
          color: _c.set(hot ? 0xffd9a0 : f.color), intensity: hot ? 4 : rnd(2.2, 3),
          life: rnd(1, 1.8), size: hot ? 0.032 : rnd(0.045, 0.07), sizeEnd: 0.01,
          gravity: -0.12, drag: 0.6, shape: hot ? Shape.Spark : Shape.Glow, stretch: 0.03, fade: 0.9,
        });
      }
    }
  }
}
